import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CreateGoldenPathRequest,
  GoldenPathDetail,
  GoldenPathForTemplate,
  GoldenPathListItem,
  GoldenPathPage,
  GuidelinesAiContext,
  GuidelinesAiResponse,
  UpdateGoldenPathRequest,
  UpdateTemplateLinksRequest,
  UpsertGoldenPathPageRequest,
} from '@internal/plugin-golden-paths-common';
import { GoldenPathsApi } from '@internal/plugin-golden-paths';

export { goldenPathsApiRef } from '@internal/plugin-golden-paths';

export class GoldenPathsClient implements GoldenPathsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('golden-paths');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message =
        typeof body.error === 'string'
          ? body.error
          : typeof body.error === 'object' &&
              body.error &&
              typeof (body.error as { message?: unknown }).message === 'string'
            ? (body.error as { message: string }).message
            : `HTTP ${response.status}: ${response.statusText}`;

      throw new Error(message);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async listPaths(): Promise<GoldenPathListItem[]> {
    const result = await this.request<{ paths: GoldenPathListItem[] }>('/paths');
    return result.paths;
  }

  async listPathsForTemplate(
    templateEntityRef: string,
  ): Promise<GoldenPathForTemplate[]> {
    const params = new URLSearchParams({ templateEntityRef });
    const result = await this.request<{ paths: GoldenPathForTemplate[] }>(
      `/template-links?${params.toString()}`,
    );
    return result.paths;
  }

  async getPath(slug: string): Promise<GoldenPathDetail> {
    return this.request<GoldenPathDetail>(`/paths/${encodeURIComponent(slug)}`);
  }

  async createPath(input: CreateGoldenPathRequest): Promise<GoldenPathDetail> {
    return this.request<GoldenPathDetail>('/paths', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updatePath(
    slug: string,
    input: UpdateGoldenPathRequest,
  ): Promise<GoldenPathDetail> {
    return this.request<GoldenPathDetail>(
      `/paths/${encodeURIComponent(slug)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
    );
  }

  async archivePath(slug: string): Promise<void> {
    await this.request<void>(`/paths/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
    });
  }

  async upsertPage(
    slug: string,
    pageSlug: string,
    input: UpsertGoldenPathPageRequest,
  ): Promise<GoldenPathPage> {
    return this.request<GoldenPathPage>(
      `/paths/${encodeURIComponent(slug)}/pages/${encodeURIComponent(pageSlug)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
    );
  }

  async updateTemplateLinks(
    slug: string,
    input: UpdateTemplateLinksRequest,
  ): Promise<string[]> {
    const result = await this.request<{ templateEntityRefs: string[] }>(
      `/paths/${encodeURIComponent(slug)}/template-links`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      },
    );
    return result.templateEntityRefs;
  }

  async improveGuidelines(input: {
    guidelines: string;
    context?: GuidelinesAiContext;
  }): Promise<GuidelinesAiResponse> {
    return this.request<GuidelinesAiResponse>('/ai/improve-guidelines', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async generateGuidelines(input: {
    prompt: string;
    context?: GuidelinesAiContext;
  }): Promise<GuidelinesAiResponse> {
    return this.request<GuidelinesAiResponse>('/ai/generate-guidelines', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async chatGuidelines(input: {
    message: string;
    guidelines?: string;
    context?: GuidelinesAiContext;
  }): Promise<GuidelinesAiResponse> {
    return this.request<GuidelinesAiResponse>('/ai/chat-guidelines', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }
}
